/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState, useCallback, type KeyboardEvent } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { CyphieMode } from '../../../../../../state/LabsAtoms';
import { InputField } from './InputField';
import { ModeSelectorPopup } from './ModeSelectorPopup';
import { SubmitButton } from './SubmitButton';

const styles = cssMap({
	inputContainer: {
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		backgroundColor: token('elevation.surface'),
	},
	outerRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
});

export type ChatInputProps = {
	onSend: (message: string) => void;
	placeholder?: string;
	disabled?: boolean;
	selectedMode: CyphieMode;
	external?: boolean;
	onModeChange: (mode: CyphieMode) => void;
	testId?: string;
};

export const ChatInput = ({
	onSend,
	placeholder = 'Describe what you need help with...',
	disabled = false,
	selectedMode,
	external = false,
	onModeChange,
	testId = 'chat-input',
}: ChatInputProps): JSX.Element => {
	const [value, setValue] = useState('');

	const handleOnSubmit = useCallback(() => {
		const trimmed = value.trim();
		if (trimmed) {
			onSend(trimmed);
			setValue('');
		}
	}, [value, onSend]);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleOnSubmit();
			}
		},
		[handleOnSubmit],
	);

	return (
		<Box xcss={styles.inputContainer} testId={testId}>
			<Flex xcss={styles.outerRow}>
				<InputField
					value={value}
					onChange={setValue}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					disabled={disabled}
					testId={`${testId}-input-field`}
				/>
				<ModeSelectorPopup
					selectedMode={selectedMode}
					onModeChange={onModeChange}
					external={external}
				/>
				<SubmitButton
					onClick={handleOnSubmit}
					disabled={disabled || !value.trim()}
					testId={`${testId}-submit-button`}
				/>
			</Flex>
		</Box>
	);
};
